import { FormControl, FormHelperText, makeStyles } from '@material-ui/core';
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExactTagsWithExclusionTagsFilter } from '../../../generated/model/exactTagsWithExclusionTagsFilter';
import { Classifier } from '../../../generated/model/models';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import InputAutocomplete, { AutocompleteOption } from '../../Inputs/Autocomplete';
import {
  formatClassifiersToOptions,
  mapSelectedAutocompleteOptions,
} from '../../Inputs/Autocomplete/autocomplete.utils';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import SwitchCompaniesUnknowns from '../../Inputs/SwitchCompaniesUnknowns';

interface Props {
  root?: boolean;
  withUnknownSwitch?: boolean;
  unknownSwitchEnabled?: boolean;
  items?: Classifier[];
  defaultValue?: ExactTagsWithExclusionTagsFilter;
  onChange: (value: ExactTagsWithExclusionTagsFilter) => void;
  active: boolean;
}

const AutocompleteTagsExclusionFilterInput: React.FC<Props> = React.memo(
  ({
    root,
    active,
    withUnknownSwitch = false,
    unknownSwitchEnabled,
    items = [],
    defaultValue = withUnknownSwitch
      ? {
          includeEmpty: unknownSwitchEnabled === undefined ? true : unknownSwitchEnabled,
          value: {
            tags: {
              includeValues: [],
              excludeValues: [],
            },
          },
        }
      : {
          value: {
            tags: {
              includeValues: [],
              excludeValues: [],
            },
          },
        },
    onChange,
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<ExactTagsWithExclusionTagsFilter>(defaultValue);
    const [toggleOpenState, setTogglerOpenState] = useState<boolean>(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options: AutocompleteOption[] = useMemo(() => formatClassifiersToOptions(items), []);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    function getValuesNames(value: string[]) {
      const valuesList = value.map((v) => options.find((o) => o.value === v)?.label);
      return valuesList.join(', ');
    }

    const rs: any[] = [];
    if (withUnknownSwitch && state.includeEmpty) {
      rs.push({ label: 'Included:', value: 'Companies with unknown data' });
    }

    if (
      state.value?.tags?.includeValues?.length ||
      (state.value?.tags && !Array.isArray(state.value.tags.includeValues))
    ) {
      rs.push({ label: 'Included:', value: getValuesNames(state.value.tags.includeValues ?? []) });
    }
    if (
      state.value?.tags?.excludeValues?.length ||
      (state.value?.tags && !Array.isArray(state.value.tags.excludeValues))
    ) {
      rs.push({
        label: 'NOT included:',
        value: getValuesNames(state.value.tags.excludeValues ?? []),
      });
    }

    return (
      <>
        {active ? (
          <>
            {withUnknownSwitch && (
              <SwitchCompaniesUnknowns
                onChange={(value) => {
                  setState({ ...state, includeEmpty: value });
                }}
                checked={state.includeEmpty}
              />
            )}
            <FormControl className={`form-control ${classes.root}`}>
              <InputAutocomplete
                defaultValue={mapSelectedAutocompleteOptions(
                  options,
                  state.value?.tags!!.includeValues,
                )}
                onChange={(includeValue) => {
                  setState({
                    ...state,
                    value: {
                      tags: {
                        includeValues: includeValue.map(
                          (option: AutocompleteOption) => option.value,
                        ),
                        excludeValues:
                          state.value && state.value.tags ? state.value.tags.excludeValues : [],
                      },
                    },
                  });
                }}
                options={options}
              />
              <div
                className={classes.tagToggler}
                onClick={() => setTogglerOpenState(!toggleOpenState)}
              >
                <FormHelperText>{t('app:notIncluded')}</FormHelperText>
                {toggleOpenState ? <ArrowDropUp /> : <ArrowDropDown />}
              </div>

              {toggleOpenState ? (
                <InputAutocomplete
                  defaultValue={mapSelectedAutocompleteOptions(
                    options,
                    state.value?.tags!!.excludeValues,
                  )}
                  onChange={(excludeValue) => {
                    setState({
                      ...state,
                      value: {
                        tags: {
                          excludeValues: excludeValue.map(
                            (option: AutocompleteOption) => option.value,
                          ),
                          includeValues:
                            state.value && state.value.tags ? state.value.tags.includeValues : [],
                        },
                      },
                    });
                  }}
                  options={options}
                />
              ) : null}
            </FormControl>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme) => ({
  root: {
    '.Mui-expanded': {
      margin: 0,
    },
  },
  tagToggler: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.fontSize - 2,
  },
}));

export default AutocompleteTagsExclusionFilterInput;
